import { Stack } from "@mantine/core";
import { PageHeader } from "components";
import PageContent from "components/layouts/PageContent";
import CustomText from "components/ui/CustomText/CustomText";
import { useAbility } from "configs/casl";
import { DebtParamsType, DebtResponseType } from "constants/types/debt";
import { MRT_RowSelectionState, MRT_Updater } from "mantine-react-table";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { canAccessRequirement } from "utils/helpers/authorization";
import { useTableParams } from "utils/hooks/common/useTableParams";
import { useDebts } from "utils/hooks/request/useDebt";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import BulkReceiptCreateForm from "./components/BulkReceiptCreateForm/BulkReceiptCreateForm";
import DebtActions from "./components/DebtActions/DebtActions";
import DebtSelectionBar from "./components/DebtSelectionBar/DebtSelectionBar";
import DebtTable from "./components/DebtTable/DebtTable";
import { DEBT_PAGE_TITLE } from "./config";

const DebtWebLayout: FC = () => {
  const ability = useAbility();
  const {
    params,
    setParams,
    handlePageChange,
    handleLimitChange,
    handleSearch,
  } = useTableParams<DebtParamsType>();
  const { debts, pagination, isLoading, mutate } = useDebts(params);
  const { openCrudModal, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const [selectedDebtById, setSelectedDebtById] = useState<
    Record<string, DebtResponseType>
  >({});

  const canBulkSelect = canAccessRequirement(ability, "payment_journal:create");

  const rowSelection: MRT_RowSelectionState = useMemo(
    () =>
      Object.fromEntries(Object.keys(selectedDebtById).map((id) => [id, true])),
    [selectedDebtById],
  );

  const handleRowSelectionChange = useCallback(
    (updater: MRT_Updater<MRT_RowSelectionState>) => {
      setSelectedDebtById((prev) => {
        const prevSelection: MRT_RowSelectionState = {};
        Object.keys(prev).forEach((id) => {
          prevSelection[id] = true;
        });
        const nextSelection =
          typeof updater === "function" ? updater(prevSelection) : updater;

        const next: Record<string, DebtResponseType> = {};
        Object.keys(nextSelection).forEach((id) => {
          if (!nextSelection[id]) return;
          // Giữ nguyên object đã chọn từ trang trước; chỉ tra cứu dữ liệu mới
          // từ trang hiện tại khi id chưa từng được chọn.
          const existing = prev[id] ?? (debts ?? []).find((d) => d.id === id);
          if (existing) next[id] = existing;
        });
        return next;
      });
    },
    [debts],
  );

  const handleClearSelection = useCallback(() => setSelectedDebtById({}), []);

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleOpenBulkModal = useCallback(() => {
    const snapshot = Object.values(selectedDebtById);
    if (snapshot.length === 0) return;

    const modalId = openCrudModal(
      <CustomText variant="section-title">Tạo nhiều phiếu thu</CustomText>,
      {
        mode: "create",
        // Modal tự vẽ footer theo từng bước (Hủy/Tiếp theo, Quay lại/Tạo phiếu thu).
        hideFooter: true,
        children: (
          <BulkReceiptCreateForm
            debts={snapshot}
            mutate={mutate}
            onClose={handleCloseModal}
            onSuccess={handleClearSelection}
          />
        ),
      },
      "95vw",
    );
    activeModalId.current = modalId;
  }, [
    selectedDebtById,
    openCrudModal,
    handleCloseModal,
    mutate,
    handleClearSelection,
  ]);

  const handleFiltersChange = useCallback(
    (filters: Partial<DebtParamsType>) => {
      setParams((prev) => ({ ...prev, ...filters, page: 1 }));
    },
    [setParams],
  );

  const selectedDebts = useMemo(
    () => Object.values(selectedDebtById),
    [selectedDebtById],
  );

  return (
    <Stack gap="sm">
      <PageHeader breadcrumbs={[{ title: DEBT_PAGE_TITLE, href: "" }]} />
      <PageContent>
        <Stack gap="xs">
          <DebtActions
            params={params}
            onSearch={handleSearch}
            onFilterChange={handleFiltersChange}
          />
          {canBulkSelect && (
            <DebtSelectionBar
              selectedDebts={selectedDebts}
              onClear={handleClearSelection}
              onOpenBulkModal={handleOpenBulkModal}
            />
          )}
          <DebtTable
            debts={debts}
            pagination={pagination}
            isLoading={isLoading}
            mutate={mutate}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            canBulkSelect={canBulkSelect}
            rowSelection={rowSelection}
            onRowSelectionChange={handleRowSelectionChange}
          />
        </Stack>
      </PageContent>
    </Stack>
  );
};

export default DebtWebLayout;
